import axios from 'axios';
import config from '../config';
import { showError } from '../utils';

import { store } from '../store';

// Generic network call function to handle common API request patterns
function networkCall({ method, url, headers, params, data, ...extraOptions }) {
  return axios({
    method,
    url,
    headers,
    params,
    data,
    ...extraOptions,
  })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const error = new Error('Invalid response');
        error.data = responseBody;
        throw error;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function reimbursementCategory(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/categories`,
    headers: {
      authorization: authToken,
    },
  });
}

export function reimbursementCategoryDetail(categoryId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/procedures/${categoryId}`,
    headers: {
      authorization: authToken,
    },
  });
}

export function uploadReimbursementDocuments(formData, authToken) {
  return axios
    .post(
      `${config.apiBaseUrl}/reimbursements/v2/reimbursements/documents`,
      formData,
      { headers: { authorization: authToken } }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      showError(err);
      throw err;
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function reimbursementRequestSubmitV2(data, authToken) {
  return axios
    .post(`${config.apiBaseUrl}/reimbursements/v2/reimbursements-v2`, data, {
      headers: {
        authorization: authToken,
        'Content-Type': 'application/json',
      },
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.errorCode === 'no-bank-details') {
        return responseBody;
      }
      if (responseBody.message === 'success') {
        return responseBody;
      } else if (responseBody.message === 'warn') {
        const err = new Error('Invalid response');
        err.data = responseBody;
        err.data.errorHeader = 'Warning';
        err.data.errorMessage = responseBody.warningMessage;
        showError(err);
        return responseBody;
      } else {
        if (responseBody.hospitalization) {
          return responseBody;
        }
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function reimbursementRequestSubmit(formData, authToken) {
  // Using direct axios call since this function has special response handling
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(`${config.apiBaseUrl}/reimbursements/v2/reimbursements`, formData, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.errorCode === 'no-bank-details') {
        return responseBody;
      }
      if (responseBody.message === 'success') {
        return responseBody;
      } else if (responseBody.message === 'warn') {
        const err = new Error('Invalid response');
        err.data = responseBody;
        err.data.errorHeader = 'Warning';
        err.data.errorMessage = responseBody.warningMessage;
        showError(err);
        return responseBody;
      } else {
        if (responseBody.hospitalization) {
          return responseBody;
        }
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function reimbursementCategoryDetailV1(categoryId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/procedures/${categoryId}`,
    headers: {
      authorization: authToken,
    },
  });
}

export function reimbursementRequestSubmitV1(formData, authToken) {
  // Using direct axios call since this function has special response handling
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(`${config.apiBaseUrl}/reimbursements/reimbursements`, formData, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else if (responseBody.message === 'warn') {
        const err = new Error('Invalid response');
        err.data = responseBody;
        err.data.errorHeader = 'Warning';
        err.data.errorMessage = responseBody.warningMessage;
        showError(err);
        return responseBody;
      } else {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function updateBankDetails(formData, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/users/addBankDetails`,
    data: formData,
    headers: {
      authorization: authToken,
    },
  });
}

export function verifyBankDetails(accountNumber, ifsc, authToken) {
  const rawData = JSON.stringify({
    accountNumber,
    ifsc,
  });
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/claims-support/verify-bank-details`,
    data: rawData,
    headers: {
      'Content-Type': 'application/json',
      authorization: authToken,
    },
  });
}

export function getDisclaimer(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/fullfilment-config`,
    headers: {
      authorization: authToken,
    },
  });
}

export function emergencyReimbursement(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/emergency-reimbursement`,
    headers: {
      authorization: authToken,
    },
  });
}

export function reimbursementSteps(categoryId, authToken, emergencyCase = 0) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/steps?categoryId=${categoryId}&emergencyCase=${emergencyCase}`,
    headers: {
      authorization: authToken,
    },
  });
}

export function reimburseUserRelative(authToken, procedureId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/user-relatives${
      procedureId ? `?procedureId=${procedureId}` : ''
    }`,
    headers: {
      authorization: authToken,
    },
  });
}

export function reimburseCategoryProcedure(authToken, categoryId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/procedures/${categoryId}`,
    headers: {
      authorization: authToken,
    },
  });
}

export function getOnlineConsultDoctorsForReimbursement(authToken, relativeId) {
  let url = `${config.apiBaseUrl}/reimbursements/v2/my-online-consults`;

  if (relativeId) {
    url = `${config.apiBaseUrl}/reimbursements/v2/my-online-consults?relativeId=${relativeId}`;
  }

  return networkCall({
    method: 'get',
    url: url,
    headers: {
      authorization: authToken,
    },
  });
}

export function getOfflineConsultDoctorsForReimbursement(authToken, relativeId) {
  let url = `${config.apiBaseUrl}/reimbursements/v2/my-offline-consults`;

  if (relativeId) {
    url = `${config.apiBaseUrl}/reimbursements/v2/my-offline-consults?relativeId=${relativeId}`;
  }

  return networkCall({
    method: 'get',
    url: url,
    headers: {
      authorization: authToken,
    },
  });
}

export function getPrescriptionGuidelines(authToken, procedureId, docType) {
  return networkCall({
    method: 'get',
    url: `${
      config.apiBaseUrl
    }/reimbursements/v2/guidelines?procedureId=${procedureId}${
      docType ? `&docType=${docType}` : ''
    }`,
    headers: {
      authorization: authToken,
    },
  });
}

export function getDeficiencyQueryCount(authToken, reimbursementId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/fetch-reimbursement-deficiency-query-count/?reimbursementId=${reimbursementId}`,
    headers: {
      authorization: authToken,
    },
  });
}

export function getDeficiencyQueryHistory(
  authToken,
  deficientId,
  reimbursementId
) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/fetch-reimbursement-deficiency-history/?deficientId=${deficientId}&reimbursementId=${reimbursementId}`,
    headers: {
      authorization: authToken,
    },
  });
}

export function cashlessSavingDetails(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/cashless-savings/details`,
    headers: {
      authorization: authToken,
    },
  });
}

export function cashlessSavingDetailsCategoryWise(authToken, categoryType) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/cashless-savings/cashless/categories?categoryType=${categoryType}`,
    headers: {
      authorization: authToken,
    },
  });
}

export function cashlessSavingLostDetailsCategoryWise(authToken, categoryType) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/cashless-savings/reimbursement/categories?categoryType=${categoryType}`,
    headers: {
      authorization: authToken,
    },
  });
}

export function empaneledDoctorsAndLabs(authToken, categoryType, requestId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/cashless-savings/categories/empanelled?categoryType=${categoryType}&requestId=${requestId}`,
    headers: {
      authorization: authToken,
    },
  });
}

export function postReasonsToSelectReimbursement(
  authToken,
  questionId,
  requestId
) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/cashless-savings/save-reimbursement-response`,
    data: { questionId, requestId },
    headers: {
      authorization: authToken,
    },
  });
}

export function nonEmpaneledDoctorsAndLabs(authToken, categoryType, sourceId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/cashless-savings/categories/non-empanelled?categoryType=${categoryType}&sourceId=${sourceId}`,
    headers: {
      authorization: authToken,
    },
  });
}

export function walletBalance(authToken, date, procedureId, relativeId) {
  const data = { date, procedureId };
  if (relativeId) data.relativeId = relativeId;
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/reimbursements/v2/wallet-balance`,
    data,
    headers: {
      authorization: authToken,
    },
  });
}

export function submitDeficientDocuments(authToken, claimId, deficientId) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/gmc/${claimId}/deficiency/${deficientId}/submit`,
    data: { claimId, deficientId },
    headers: {
      authorization: authToken,
    },
  });
}

export function checkReimbursementEligibility(authToken, lat, lng) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/vision/reimbursement-eligibility`,
    params: { lat, long: lng },
    headers: {
      authorization: authToken,
    },
  });
}
